import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FaBell, FaCamera, FaUpload, FaTimes, FaUserTie } from "react-icons/fa";
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

// Styled Components with responsive design
const ManagerPortal = styled.div`
  width: 100%;
  background-color: #f9fafb;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(155, 72, 72, 0.1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;

  @media (max-width: 1450px) {
    width: 100%;
  }
`;

const Header = styled.header`
  background-color: #2196F3;
  border-bottom: 1px solid #e5e7eb;
  padding: 12px 16px;
  
  @media (min-width: 768px) {
    padding: 16px 24px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const Logo = styled.div`
  width: 28px;
  height: 28px;
  background-color: #2563eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    width: 32px;
    height: 32px;
  }
  
  span {
    color: white;
    font-weight: bold;
    font-size: 12px;
    
    @media (min-width: 768px) {
      font-size: 14px;
    }
  }
  
  .logo-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const PortalTitle = styled.h1`
  font-size: 14px;
  font-family: 'Albert Sans', sans-serif;
  font-weight: 500;
  color: #ffffff;
  margin: 0;
  
  @media (min-width: 480px) {
    font-size: 16px;
  }
  
  @media (min-width: 768px) {
    font-size: 18px;
  }
  
  @media (min-width: 1024px) {
    font-size: 20px;
  }
  
  @media (max-width: 380px) {
    font-size: 12px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  
  @media (min-width: 768px) {
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const NotificationWrapper = styled.div`
  position: relative;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  
  @media (min-width: 768px) {
    padding: 8px;
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background-color: #dc2626;
  color: white;
  border-radius: 50%;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: bold;
  border: 2px solid #2196F3;
  padding: 0 3px;
  animation: pulse 2s infinite;
  
  @media (min-width: 768px) {
    top: 2px;
    right: 2px;
    min-width: 18px;
    height: 18px;
    font-size: 10px;
    padding: 0 4px;
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`;

const UserInfo = styled.div`
  text-align: right;
  display: none;
  
  @media (min-width: 640px) {
    display: block;
  }
`;

const UserName = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const UserRole = styled.p`
  font-size: 11px;
  color: #ffffff;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 12px;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  
  @media (min-width: 768px) {
    gap: 12px;
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;
  
  @media (min-width: 768px) {
    width: 40px;
    height: 40px;
  }
  
  &:hover {
    border-color: #2563eb;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  span {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    
    @media (min-width: 768px) {
      font-size: 14px;
    }
  }
`;

const LogoutButton = styled.button`
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  @media (min-width: 640px) {
    display: flex;
  }
  
  @media (min-width: 768px) {
    gap: 8px;
    padding: 8px 16px;
    font-size: 14px;
  }
  
  &:hover {
    background-color: #dc2626;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    width: 14px;
    height: 14px;
    
    @media (min-width: 768px) {
      width: 16px;
      height: 16px;
    }
  }
  
  span {
    @media (max-width: 767px) {
      display: none;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @media (min-width: 768px) {
    width: 16px;
    height: 16px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const UserDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 180px;
  overflow: hidden;
  animation: dropdownSlide 0.2s ease-out;
  
  @media (min-width: 768px) {
    min-width: 200px;
  }
  
  @keyframes dropdownSlide {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    right: 20px;
    width: 12px;
    height: 12px;
    background: white;
    border-left: 1px solid #e5e7eb;
    border-top: 1px solid #e5e7eb;
    transform: rotate(45deg);
  }
`;

const DropdownHeader = styled.div`
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  background-color: #f9fafb;
  
  @media (min-width: 768px) {
    padding: 16px;
  }
  
  h4 {
    margin: 0 0 4px 0;
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    
    @media (min-width: 768px) {
      font-size: 14px;
    }
  }
  
  p {
    margin: 0;
    font-size: 11px;
    color: #6b7280;
    word-break: break-word;
    
    @media (min-width: 768px) {
      font-size: 12px;
    }
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: none;
  text-align: left;
  color: #374151;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (min-width: 768px) {
    padding: 12px 16px;
    font-size: 18px;
    gap: 12px;
  }

  @media (min-width: 400px) {
    padding: 12px 16px;
    font-size: 12px;
    gap: 10px;
  }
  
  &:hover {
    background-color: #f3f4f6;
  }
  
  &.danger:hover {
    background-color: #fef2f2;
    color: #b91c1c;
  }
`;

const Navigation = styled.nav`
  background-color: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (min-width: 768px) {
    padding: 0 24px;
  }
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const NavTabs = styled.div`
  display: flex;
  gap: 16px;
  min-width: min-content;
  
  @media (min-width: 768px) {
    gap: 24px;
  }
  
  @media (min-width: 1024px) {
    gap: 32px;
  }
`;

const NavTab = styled.button`
  padding: 12px 6px;
  border: none;
  background: none;
  font-family: 'Albert Sans', sans-serif;
  font-size: 13px;
  font-weight: 550;
  color: ${props => props.$active ? '#2563eb' : '#6b7280'};
  border-bottom: 2px solid ${props => props.$active ? '#2563eb' : 'transparent'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    padding: 14px 8px;
    font-size: 14px;
  }
  
  @media (min-width: 1024px) {
    padding: 16px 8px;
  }

  &:hover {
    color: #374151;
  }
`;

const ProfileModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: modalFadeIn 0.2s ease-out;
  padding: 16px;
  backdrop-filter: blur(4px);
  
  @keyframes modalFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ProfileModalContent = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 0;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.3s ease-out;
  overflow: hidden;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ProfileModalHeader = styled.div`
  background: rgba(255, 255, 255, 0.95);
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin: 0;
    color: #1f2937;
    font-size: 20px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const ProfileModalBody = styled.div`
  background: white;
  padding: 32px 24px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: #1f2937;
  }
`;

const ProfileContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  
  @media (min-width: 768px) {
    gap: 16px;
  }
`;

const ProfileAvatar = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #667eea;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
  background: ${props => props.$hasImage ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  span {
    font-size: 48px;
    font-weight: 600;
    color: white;
  }
`;

const ProfileInfo = styled.div`
  text-align: center;
  margin-bottom: 20px;
  
  h3 {
    margin: 0 0 4px 0;
    color: #111827;
    font-size: 15px;
    font-weight: 600;
    word-break: break-word;
    
    @media (min-width: 768px) {
      font-size: 16px;
    }
  }
  
  p {
    margin: 0;
    color: #6b7280;
    font-size: 13px;
    word-break: break-word;
    
    @media (min-width: 768px) {
      font-size: 14px;
    }
  }
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s;
  border: none;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
    }
  }
  
  &.secondary {
    background: white;
    color: #667eea;
    border: 2px solid #667eea;
    
    &:hover:not(:disabled) {
      background: #667eea;
      color: white;
    }
  }
  
  &.outline {
    background: white;
    color: #6b7280;
    border: 2px solid #e5e7eb;
    
    &:hover:not(:disabled) {
      border-color: #9ca3af;
      color: #1f2937;
    }
  }
`;

const InfoBox = styled.div`
  margin-top: 20px;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  
  p {
    margin: 0;
  }
`;

const UserIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const SettingsIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ManageNavbar = ({ currentPage = "Dashboard" }) => {
  const [activeTab, setActiveTab] = useState(currentPage);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [profilePicture, setProfilePicture] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  
  const navigate = useNavigate();
  const logoutAttempted = useRef(false);
  const profileFileInputRef = useRef(null);
  const lastClickTime = useRef(0);

  const tabPathMap = {
    Dashboard: "/Hrdashboard",
    Employees: "/Hrempdash",
    Report: "/Report",
    Configuration: "/Configuration",
    "Hr Timesheet": "/Hrtimesheet",
  };

  useEffect(() => {
    fetchUserInfo();
    fetchProfilePicture();
  }, []);

  useEffect(() => {
    fetchNotificationCount();
    
    const interval = setInterval(() => {
      fetchNotificationCount();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/user', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('✅ User Data:', userData);
        setUserInfo(userData);
        setError(null);
      } else if (response.status === 401) {
        window.location.href = '/';
      } else {
        throw new Error('Failed to fetch user info');
      }
    } catch (err) {
      console.error('❌ Error fetching user info:', err);
      setError(err.message);
      setUserInfo({
        name: 'User',
        email: 'user@company.com',
        profile_picture: '',
        role: 'HR Manager'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProfilePicture = async () => {
    try {
      const response = await fetch('/api/user/profile-picture', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfilePicture(data.profile_picture);
      }
    } catch (err) {
      console.error('Error fetching profile picture:', err);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      console.log('🔔 Navbar - Fetching notification count...');
      
      const response = await fetch('/api/timesheet/hr/pending-approvals', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('❌ Navbar - Failed to fetch pending submissions:', response.status);
        setNotificationCount(0);
        return;
      }

      const data = await response.json();
      const submissions = data.submissions || [];
      
      console.log('📊 Navbar - Total submissions received:', submissions.length);

      const pendingCount = submissions.filter(submission => 
        submission.status === 'pending' || submission.status === 'edited'
      ).length;

      console.log('🔔 Navbar - Pending/Edited submissions count:', pendingCount);
      setNotificationCount(pendingCount);
      
    } catch (err) {
      console.error('❌ Navbar - Error fetching notification count:', err);
      setNotificationCount(0);
    }
  };

  const handleNotificationClick = () => {
    console.log('🔔 Navbar - Notification icon clicked');
    setNotificationCount(0);
    console.log('🔀 Navbar - Navigating to /Hrtimesheet');
    setActiveTab("HrTimesheet");
    navigate("/Hrtimesheet", { replace: true });
  };

  const handleNavigation = (tab) => {
    try {
      const path = tabPathMap[tab];
      if (path) {
        console.log(`🔀 Navbar - Navigating to ${tab} (${path})`);
        setActiveTab(tab);
        navigate(path, { replace: true });
      }
    } catch (error) {
      console.error("❌ Navbar - Navigation error:", error);
    }
  };

  const handleAvatarClick = () => {
    const currentTime = Date.now();
    const timeDiff = currentTime - lastClickTime.current;
    
    // Double click detection (within 300ms)
    if (timeDiff < 300) {
      console.log('🖱️ Double click detected - Opening profile picture modal');
      setShowProfilePictureModal(true);
      setShowDropdown(false);
      lastClickTime.current = 0; // Reset
    } else {
      // Single click - show dropdown
      setShowDropdown(!showDropdown);
      lastClickTime.current = currentTime;
    }
  };

  const handleProfileFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadProfilePicture = async () => {
    if (!previewImage) return;

    setIsUploadingProfile(true);
    try {
      const response = await fetch('/api/user/profile-picture', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile_picture: previewImage
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProfilePicture(data.profile_picture);
        setShowProfilePictureModal(false);
        setPreviewImage(null);
        alert('Profile picture updated successfully!');
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to upload profile picture');
      }
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      alert('Failed to upload profile picture');
    } finally {
      setIsUploadingProfile(false);
    }
  };

  const handleUseEmailAvatar = async () => {
  setIsUploadingProfile(true);
  try {
    const response = await fetch('/api/user/profile-picture', {
      method: 'DELETE',
      credentials: 'include',
    });

    if (response.ok) {
      setProfilePicture(null);
      setShowProfilePictureModal(false);
      setPreviewImage(null);
      alert('Switched to default logo successfully!');
    } else {
      alert('Failed to switch to default logo');
    }
  } catch (err) {
    console.error('Error switching to default logo:', err);
    alert('Failed to switch to default logo');
  } finally {
    setIsUploadingProfile(false);
  }
};

  const handleLogout = async () => {
    if (isLoggingOut || logoutAttempted.current) {
      console.log('⏸️ Logout already in progress, skipping...');
      return;
    }

    try {
      logoutAttempted.current = true;
      setIsLoggingOut(true);
      setShowDropdown(false);
      
      console.log('🚪 HR Navbar logout initiated...');
      
      if (userInfo) {
        setUserInfo({
          ...userInfo,
          name: 'Logging out...'
        });
      }
      
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch('/auth/logout', {
          method: 'GET',
          credentials: 'include',
          headers: { 
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        console.log('📡 Backend logout response:', response.status);
        
      } catch (fetchError) {
        console.warn('⚠️ Backend logout error (continuing):', fetchError.message);
      }
      
      try {
        if (typeof Storage !== "undefined" && sessionStorage) {
          const keysToRemove = [
            'auth_user_data',
            'auth_user_role', 
            'auth_last_check',
            'auth_state',
            'auth_manager_info',
            'rate_limit_backoff'
          ];
          
          keysToRemove.forEach(key => {
            sessionStorage.removeItem(key);
          });
          
          sessionStorage.clear();
          console.log('✅ Session storage cleared');
        }
        
        if (typeof Storage !== "undefined" && localStorage) {
          localStorage.clear();
          console.log('✅ Local storage cleared');
        }
        
        const domains = [
          '.manurevasolutions.com',
          'leave-management.manurevasolutions.com',
          'api.leave-management.manurevasolutions.com',
          window.location.hostname,
          ''
        ];
        
        const cookiesToClear = [
          'leavemgmt_session',
          'session',
          'auth_token',
          'connect.sid'
        ];
        
        domains.forEach(domain => {
          cookiesToClear.forEach(cookie => {
            document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}; SameSite=None; Secure`;
            document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure`;
          });
        });
        
        document.cookie.split(";").forEach((c) => {
          const cookie = c.trim();
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
          
          domains.forEach(domain => {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
          });
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
        });
        
        console.log('✅ All cookies cleared');
        
      } catch (storageError) {
        console.error('❌ Storage clearing error:', storageError);
      }

      await new Promise(resolve => setTimeout(resolve, 200));
      
      console.log('✅ Cleanup complete, redirecting...');
      
      const timestamp = Date.now();
      const logoutUrl = `/?logout=true&t=${timestamp}`;
      
      console.log('🏠 Redirecting to:', logoutUrl);
      
      window.location.replace(logoutUrl);
      
    } catch (error) {
      console.error('❌ Critical logout error:', error);
      
      const timestamp = Date.now();
      window.location.replace(`/?logout=true&error=true&t=${timestamp}`);
    }
  };

  const handleProfileClick = () => {
    setShowDropdown(false);
    setShowProfileModal(true);
  };

  const handleCloseProfile = () => {
    setShowProfileModal(false);
  };

  const handleSettingsClick = () => {
    setShowDropdown(false);
    console.log('⚙️ Settings clicked - implement settings page navigation');
  };

  const closeProfilePictureModal = () => {
    setShowProfilePictureModal(false);
    setPreviewImage(null);
  };

  const getInitials = (name) => {
    if (!name || name === 'Loading...' || name === 'Logging out...') return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getDisplayName = () => {
    if (loading) return 'Loading...';
    if (isLoggingOut) return 'Logging out...';
    if (!userInfo?.name) return 'User';
    
    const nameParts = userInfo.name.split(' ');
    if (nameParts.length > 1 && userInfo.name.length > 15) {
      return `${nameParts[0]} ${nameParts[nameParts.length - 1].charAt(0)}.`;
    }
    return userInfo.name;
  };

  const getUserRole = () => {
    if (loading) return 'Loading...';
    if (isLoggingOut) return 'Please wait...';
    return userInfo?.role || 'HR Manager';
  }; 

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.user-dropdown')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  useEffect(() => {
    const handleModalClickOutside = (event) => {
      if (showProfileModal && event.target === event.currentTarget) {
        setShowProfileModal(false);
      }
    };

    if (showProfileModal) {
      document.addEventListener('mousedown', handleModalClickOutside);
      return () => document.removeEventListener('mousedown', handleModalClickOutside);
    }
  }, [showProfileModal]);

  return (
    <>
      <ManagerPortal>
        <Header>
          <HeaderContent>
            <HeaderLeft>
              <Logo>
                <span><img src="/Images/Manurevalogo.png" alt="MG" className="logo-image" /></span>
              </Logo>
              <PortalTitle>Manureva Digital Solutions</PortalTitle>
            </HeaderLeft>

            <HeaderRight>
              <NotificationWrapper 
                onClick={handleNotificationClick} 
                title={`${notificationCount} pending timesheet${notificationCount !== 1 ? 's' : ''}`}
              >
                <FaBell style={{ fontSize: '18px', color: '#ffffff' }} />
                {notificationCount > 0 && (
                  <NotificationBadge>
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </NotificationBadge>
                )}
              </NotificationWrapper>
              
              <UserInfo>
                <UserName style={{fontFamily: 'Albert Sans, sans-serif'}}>
                  {getDisplayName()}
                </UserName>
                <UserRole>{getUserRole()}</UserRole>
              </UserInfo>

              <UserSection>
                <UserDropdown className="user-dropdown">
                  <UserAvatar 
  onClick={handleAvatarClick}
  title="Double-click to change profile picture"
>
  {loading ? (
    <LoadingSpinner />
  ) : (
    <img 
      src={profilePicture || '/Images/Manurevalogo.png'} 
      alt={userInfo?.name || 'User'}
      onError={(e) => {
        // If image fails to load, show Manureva logo
        e.target.src = '/Images/Manurevalogo.png';
      }}
    />
  )}
</UserAvatar>

                  {/* {showDropdown && !isLoggingOut && (
                    <DropdownMenu>
                      <DropdownHeader>
                        <h4>{userInfo?.name || 'User'}</h4>
                        <p>{userInfo?.email || 'user@company.com'}</p>
                      </DropdownHeader>
                      
                      <DropdownItem onClick={handleProfileClick}>
                        <UserIcon />
                        View Profile
                      </DropdownItem>
                      
                      <DropdownItem onClick={handleSettingsClick}>
                        <SettingsIcon />
                        Settings
                      </DropdownItem>
                      
                      <DropdownItem onClick={handleLogout} className="danger">
                        <PowerSettingsNewIcon style={{ width: '20px', height: '20px' }} />
                        Sign Out
                      </DropdownItem>
                    </DropdownMenu>
                  )} */}
                </UserDropdown>

                <LogoutButton 
                  onClick={handleLogout} 
                  disabled={isLoggingOut}
                  title={isLoggingOut ? 'Signing Out...' : 'Sign Out'}
                >
                  {isLoggingOut ? (
                    <>
                      <LoadingSpinner />
                      <span>Signing Out...</span>
                    </>
                  ) : (
                    <PowerSettingsNewIcon />
                  )}
                </LogoutButton>
              </UserSection>
            </HeaderRight>
          </HeaderContent>
        </Header>

        <Navigation>
          <NavTabs>
            {Object.keys(tabPathMap).map((tab) => (
              <NavTab
                key={tab}
                onClick={() => handleNavigation(tab)}
                $active={activeTab === tab}
              >
                {tab}
              </NavTab>
            ))}
          </NavTabs>
        </Navigation>

        {error && !isLoggingOut && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#fef2f2',
            borderBottom: '1px solid #fecaca',
            color: '#dc2626',
            fontSize: '11px'
          }}>
            Warning: {error}. Using fallback data.
          </div>
        )}
      </ManagerPortal>

      {/* View Profile Modal */}
      {showProfileModal && !isLoggingOut && (
        <ProfileModal onClick={handleCloseProfile}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h2 style={{ margin: 0, color: '#111827', fontSize: '18px', fontWeight: '600' }}>
                User Profile
              </h2>
              <CloseButton onClick={handleCloseProfile}>✕</CloseButton>
            </div>
            
            <ProfileContent>
              <div style={{
  width: '70px',
  height: '70px',
  borderRadius: '50%',
  overflow: 'hidden',
  border: '3px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}}>
  <img 
    src={profilePicture || '/Images/Manurevalogo.png'} 
    alt={userInfo?.name || 'User'}
    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    onError={(e) => {
      // If image fails to load, show Manureva logo
      e.target.src = '/Images/Manurevalogo.png';
    }}
  />
</div>
              
              <ProfileInfo>
                <h3>{userInfo?.name || 'User Name'}</h3>
                <p>{userInfo?.email || 'user@company.com'}</p>
                <p style={{ marginTop: '8px', fontWeight: '500' }}>
                  {getUserRole()}
                </p>
              </ProfileInfo>
            </ProfileContent>
          </div>
        </ProfileModal>
      )}

      {/* Profile Picture Upload Modal */}
      {showProfilePictureModal && (
        <ProfileModal onClick={closeProfilePictureModal}>
          <ProfileModalContent onClick={(e) => e.stopPropagation()}>
            <ProfileModalHeader>
              <h2>
                <FaCamera style={{ color: '#667eea' }} />
                Profile Picture
              </h2>
              <CloseButton onClick={closeProfilePictureModal}>
                <FaTimes />
              </CloseButton>
            </ProfileModalHeader>

            <ProfileModalBody>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
  {previewImage ? (
    <div style={{ textAlign: 'center' }}>
      <ProfileAvatar $hasImage={true}>
        <img src={previewImage} alt="Preview" />
      </ProfileAvatar>
      <p style={{ marginTop: '12px', fontSize: '14px', color: '#6b7280' }}>
        Preview
      </p>
    </div>
  ) : (
    <div style={{ textAlign: 'center' }}>
      <ProfileAvatar $hasImage={true}>
        <img 
          src={profilePicture || '/Images/Manurevalogo.png'} 
          alt="Current Profile"
          onError={(e) => {
            e.target.src = '/Images/Manurevalogo.png';
          }}
        />
      </ProfileAvatar>
      <p style={{ marginTop: '12px', fontSize: '14px', color: '#6b7280' }}>
        {profilePicture ? 'Current Picture' : 'Default Picture'}
      </p>
    </div>
  )}
</div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {previewImage ? (
                  <>
                    <ActionButton
                      className="primary"
                      onClick={handleUploadProfilePicture}
                      disabled={isUploadingProfile}
                    >
                      {isUploadingProfile ? (
                        <>
                          <LoadingSpinner />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FaUpload />
                          Save Profile Picture
                        </>
                      )}
                    </ActionButton>

                    <ActionButton
                      className="outline"
                      onClick={() => setPreviewImage(null)}
                      disabled={isUploadingProfile}
                    >
                      Cancel
                    </ActionButton>
                  </>
                ) : (
                  <>
                    <ActionButton
                      className="primary"
                      onClick={() => profileFileInputRef.current?.click()}
                      disabled={isUploadingProfile}
                    >
                      <FaUpload />
                      Upload from Device
                    </ActionButton>

                    {profilePicture && (
  <ActionButton
    className="secondary"
    onClick={handleUseEmailAvatar}
    disabled={isUploadingProfile}
  >
    <FaUserTie />
    Use Default Logo
  </ActionButton>
)}
                  </>
                )}
              </div>

              <input
                ref={profileFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfileFileChange}
                style={{ display: 'none' }}
              />

              <InfoBox>
                <p>
                  📸 Supported formats: JPG, PNG, GIF
                  <br />
                  Maximum file size: 5MB
                </p>
              </InfoBox>
            </ProfileModalBody>
          </ProfileModalContent>
        </ProfileModal>
      )}
    </>
  );
};

export default ManageNavbar;